/**
 * Autcomplete JS controller.
 *
 * This class implements a reusable autocompletion functionality
 * supported by DustPress.js and jQuery.
 */

// Use jQuery as $ within this file scope.
const $ = jQuery;

// Use lodash
import _ from 'lodash';

/**
 * Export the class reference.
 */
export default class Autocomplete {

    /**
     *
     * @param {mixed} input A jQuery selector for the input element. Optimize by passing an object reference.
     * @param {string} model The DustPress model name for the AJAX request.
     * @param {string} method The DustPress model method name for the AJAX request.
     * @param {string} partial The template file to be used with DustPress.
     * @param {mixed} container A jQuery selector for the result container element. Optimize by passing an object reference.
     * @param {function} success The success function called after the container is filled with results.
     * @param {function} error The error function called if the AJAX reqeuest fails.
     * @param {function} beforeXhr The function to be called just before an AJAX reqeuest is performed.
     */
    constructor( input, model, method, partial, container, success, error, beforeXhr ) {
        this.input = $( input );
        this.model = model;
        this.method = method;
        this.partial = partial;
        this.container = $( container );
        this.success = success;
        this.error = error;
        this.beforeXhr = beforeXhr;

        // Initialize the inner search cache.
        this.cache = {};

        // Format the DustPress.js endpoint.
        this.endpoint = this.model ? this.model + '/' + this.method : $this.method;

        // Bind JS events.
        this.events();

        // A counter to track searches.
        this.searchCount = 0;

        // Initialize the xhr.
        this.currentXhr = null;
    }

    /**
     * Bind events.
     */
    events() {
        this.input.on( 'input', ( evt ) => this.maybeSearch( evt ) );
    }

    /**
     * Check the input field if we should perform a new search.
     *
     * @param {Event} evt JS event object.
     */
    maybeSearch( evt ) {
        const target = evt.target;
        const term = target.value;

        // Display cached result.
        if ( _.has( this.cache, term ) ) {
            this.handleSuccess( this.cache[ term ]);
            return;
        }

        if ( term.length > 2 ) {
            const currentCount = ++this.searchCount;
            setTimeout( () => this.search( term, currentCount ), 500 );
        }
    }

    /**
     * Search by the search term.
     *
     * @param {string} term  The search term.
     * @param {int}    count The current search count.
     */
    search( term, count ) {

        // If there already is newer searches, bail early.
        if ( count < this.searchCount ) {
            return;
        }

        // Abort the earlier XHR.
        if ( this.currentXhr && _.isFunction( this.currentXhr.abort ) ) {
            this.currentXhr.abort();
        }

        if ( _.isFunction( this.beforeXhr ) ) {
            this.beforeXhr.call( this, term );
        }

        // Use the assigned endpoint with DustPress.js.
        this.currentXhr = dp( this.endpoint, {
            args: {
                term: term
            },
            partial: this.partial ? this.partial : null,
            tidy: true,
            success: ( response ) => {
                this.currentXhr = null;

                // Content is named by the method or it is directly the response.
                const content = _.get( response, this.method, response );

                // Cache result.
                this.cache[ term ] = content;

                // Call the user success handler.
                this.handleSuccess( content );
            },
            error: this.error
        });
    }

    /**
     * Handle successful autocompletion.
     *
     * @param {string} content The autocomplete HTML content.
     */
    handleSuccess( content ) {

        // If suggestions were found and a partial was used, display the HTML.
        if ( content && this.partial ) {
            this.container.html( content );
        }

        // Run the success function if it is callable.
        if ( _.isFunction( this.success ) ) {
            this.success.call( this, content );
        }
    }
}
